/* eslint-disable */
import { FuseNavigationItem } from '@fuse/components/navigation';
import { ADMIN_MODULE_IDS } from 'app/core/admin-module-ids';

// Extended navigation item interface with moduleId for permission checking
export interface ExtendedFuseNavigationItem extends FuseNavigationItem {
    moduleId?: number;
    anyModuleIds?: number[];
    children?: ExtendedFuseNavigationItem[];
}

// Ordering rule for this menu: the screens an admin opens all day sit at the
// top level as plain links (the acquisition funnel — leads, inquiries, demos,
// businesses), grouped sections follow in rough order of how often they are
// touched, and everything that is configuration lands in System at the bottom.
//
// Every `id` here must be unique across the whole tree: FuseNavigationService
// .getItem() returns the FIRST match, so a repeated id sends active-highlight
// and auto-expand to the wrong node.
export const defaultNavigation: ExtendedFuseNavigationItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'basic',
        icon: 'heroicons_outline:home',
        link: '/dashboard',
        moduleId: 1, // Dashboard - always accessible
    },
    // The admin's own inbox — the paged, filterable version of the header
    // bell. No moduleId: an inbox is personal, so every admin gets it (the
    // nav filter shows items without a moduleId), and the API already scopes
    // rows to the caller. NOT the "User Notifications" campaign tool under
    // Communications, which pushes to app users.
    {
        id: 'admin-notifications',
        title: 'Notifications',
        type: 'basic',
        icon: 'heroicons_outline:bell',
        link: '/admin-notifications',
    },
    // Sits next to Dashboard rather than inside System: this is the screen you
    // open when something is on fire, and it should not be two clicks deep.
    {
        id: 'server-health',
        title: 'Server Health',
        type: 'basic',
        icon: 'heroicons_outline:heart',
        link: '/server-health',
        moduleId: 67, // Server Health (Ops)
    },

    // ---------------------------------------------------------------------
    // Acquisition funnel, in the order a prospect moves through it. These are
    // top-level links rather than a "Business Management" group because they
    // are the most-opened screens in the panel.
    // ---------------------------------------------------------------------
    {
        id: 'leads',
        title: 'Leads',
        type: 'basic',
        icon: 'heroicons_outline:user-group',
        link: '/leads',
        moduleId: 5, // Leads
    },
    {
        id: 'inquiries',
        title: 'Inquiries',
        type: 'basic',
        icon: 'heroicons_outline:information-circle',
        link: '/inquiries',
        moduleId: 6, // Inquiries
    },
    {
        id: 'app-demo',
        title: 'Demo Bookings',
        type: 'basic',
        icon: 'heroicons_outline:calendar-days',
        link: '/app-demo',
        moduleId: 75, // Demo Bookings — own module since p88; reused Businesses (3) before
    },
    {
        id: 'business',
        title: 'Businesses',
        type: 'basic',
        icon: 'heroicons_outline:building-storefront',
        link: '/business',
        moduleId: 3, // Businesses
    },
    {
        id: 'partners',
        title: 'Partners',
        type: 'collapsable',
        icon: 'heroicons_outline:users',
        children: [
            {
                id: 'partner',
                title: 'Partners',
                type: 'basic',
                icon: 'heroicons_outline:users',
                link: '/partner',
                moduleId: 4, // Partners
            },
            {
                id: 'partner-leads',
                title: 'Partner Leads',
                type: 'basic',
                icon: 'heroicons_outline:user-group',
                link: '/partner-leads',
                moduleId: 4, // Partners — prospects captured in the partner app
            },
        ],
    },

    // ---------------------------------------------------------------------
    // Accounts: money owed to and paid out to sale partners, plus the tax
    // paperwork that goes with it. Deliberately NOT a home for master tables
    // or for order/wallet transactions — those stay in Master Data and in
    // Plans & Subscriptions respectively.
    //
    // Each entry has its own module (68–74, rows added by Docs/go-live/p86),
    // so a role can be given Withdrawals without Partners and vice versa.
    // Until p86 all seven reused Partners (4), which meant any role holding
    // Partners → View saw the whole section.
    // ---------------------------------------------------------------------
    {
        id: 'accounts',
        title: 'Accounts',
        type: 'collapsable',
        icon: 'heroicons_outline:banknotes',
        children: [
            {
                id: 'partner-payments',
                title: 'Partner Payments',
                type: 'basic',
                icon: 'heroicons_outline:banknotes',
                link: '/partner-payments',
                anyModuleIds: [68, 70],
            },
            {
                id: 'partner-verifications',
                title: 'Partner Verifications',
                type: 'basic',
                icon: 'heroicons_outline:shield-check',
                link: '/partner-verifications',
                moduleId: 69, // Partner Verifications — who is waiting on a human to verify them
            },
            {
                id: 'sale-partner-commission-masters',
                title: 'Commission Masters',
                type: 'basic',
                icon: 'heroicons_outline:currency-dollar',
                link: '/sales-commission',
                moduleId: 71, // Commission Masters
            },
            {
                id: 'sale-partner-tds-config',
                title: 'TDS Config',
                type: 'basic',
                icon: 'heroicons_outline:receipt-percent',
                link: '/partner-tds-config',
                moduleId: 72, // TDS Config — rate/threshold (Sec 194H)
            },
            {
                id: 'sale-partner-tds-settlement',
                title: 'Quarterly TDS Settlement',
                type: 'basic',
                icon: 'heroicons_outline:receipt-percent',
                link: '/partner-tds-settlement',
                moduleId: 73, // Quarterly TDS Settlement — admin marks the quarter as deposited
            },
            {
                id: 'sale-partner-form16a',
                title: 'Form 16A Management',
                type: 'basic',
                icon: 'heroicons_outline:document-arrow-up',
                link: '/partner-form16a',
                moduleId: 74, // Form 16A Management — quarterly TDS certificate uploads
            },
        ],
    },

    {
        id: 'plans-group',
        title: 'Plans & Subscriptions',
        type: 'collapsable',
        icon: 'heroicons_outline:trophy',
        children: [
            {
                id: 'plans',
                title: 'Plans',
                type: 'basic',
                icon: 'heroicons_outline:trophy',
                link: '/plans',
                moduleId: 7, // Plans
            },
            {
                id: 'coupon',
                title: 'Coupons',
                type: 'basic',
                icon: 'heroicons_outline:gift',
                link: '/coupon',
                moduleId: 8, // Coupons
            },
            {
                id: 'orders',
                title: 'Orders',
                type: 'basic',
                icon: 'heroicons_outline:shopping-cart',
                link: '/orders',
                moduleId: 9, // Orders
            },
            {
                id: 'order-refunds',
                title: 'Order Refunds',
                type: 'basic',
                icon: 'heroicons_outline:currency-dollar',
                link: '/order-refunds',
                moduleId: 10, // Order Refunds
            },
            {
                id: 'wallet',
                title: 'Wallet',
                type: 'basic',
                icon: 'heroicons_outline:users',
                link: '/wallet',
                moduleId: 11, // Wallet
            },
            {
                id: 'marketplace-catalog',
                title: 'Marketplace Catalog',
                type: 'basic',
                icon: 'heroicons_outline:squares-2x2',
                link: '/marketplace-catalog',
                moduleId: 7, // Plans area — per-plan statuses + prices
            },
            {
                id: 'business-addons',
                title: 'Business Add-ons',
                type: 'basic',
                icon: 'heroicons_outline:building-storefront',
                link: '/business-addons',
                moduleId: 7, // Plans area — The Shop seller side
            },
            {
                id: 'feature-import',
                title: 'Import configuration',
                type: 'basic',
                icon: 'heroicons_outline:arrow-up-tray',
                link: '/plans/feature-import',
                moduleId: 7, // Plans area — export from dev, import to prod
            },
        ],
    },

    {
        id: 'support',
        title: 'Support',
        type: 'collapsable',
        icon: 'heroicons_outline:lifebuoy',
        children: [
            {
                id: 'tickets',
                title: 'Support Tickets',
                type: 'basic',
                icon: 'heroicons_outline:lifebuoy',
                link: '/tickets',
                moduleId: 14, // Support Tickets
            },
            {
                id: 'chat-support',
                title: 'Chat Support',
                type: 'basic',
                icon: 'heroicons_outline:chat-bubble-bottom-center-text',
                link: '/chat-support',
                moduleId: 15, // Chat Support
            },
            {
                id: 'support-actions',
                title: 'Support Actions',
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-check',
                link: '/support-actions',
                moduleId: 16, // Support Actions
            },
            {
                id: 'knowledge-base',
                title: 'Knowledge Base',
                type: 'collapsable',
                icon: 'heroicons_outline:book-open',
                children: [
                    {
                        id: 'FAQs',
                        title: 'FAQs',
                        type: 'basic',
                        icon: 'heroicons_outline:question-mark-circle',
                        link: '/FAQs',
                        moduleId: 26, // FAQs
                    },
                    {
                        id: 'support-articles',
                        title: 'Support Articles',
                        type: 'basic',
                        icon: 'heroicons_outline:newspaper',
                        link: '/support-articles',
                        moduleId: 28, // Support Articles
                    },
                    {
                        id: 'support-article-types',
                        title: 'Support Article Types',
                        type: 'basic',
                        icon: 'heroicons_outline:document-text',
                        link: '/support-article-types',
                        moduleId: 27, // Support Article Types
                    },
                    {
                        id: 'ticketTopics',
                        title: 'Ticket Topics',
                        type: 'basic',
                        icon: 'heroicons_outline:building-library',
                        link: '/ticket-topics',
                        moduleId: 29, // Ticket Topics
                    },
                ],
            },
        ],
    },

    // ---------------------------------------------------------------------
    // Communications: everything that sends a message to a business or user —
    // the send itself, the channels it goes out on, and the templates it is
    // rendered from. Previously split across Notifications, Campaign Center,
    // Business Settings and Master Data.
    // ---------------------------------------------------------------------
    {
        id: 'communications',
        title: 'Communications',
        type: 'collapsable',
        icon: 'heroicons_outline:megaphone',
        children: [
            {
                id: 'notifications',
                title: 'User Notifications',
                type: 'basic',
                icon: 'heroicons_outline:bell',
                link: '/notifications',
                moduleId: 19, // User Notifications
            },
            {
                id: 'campaign-executions',
                title: 'Campaign Executions',
                type: 'basic',
                icon: 'heroicons_outline:chart-bar-square',
                link: '/campaigns/executions',
                moduleId: 66,
            },
            {
                // Sits beside campaigns rather than under them on purpose: it
                // has no schedule and no audience to pick — it is a per-person
                // sequence, and filing it as a campaign invites someone to look
                // for a Send button that does not exist.
                id: 'onboarding-reminders',
                title: 'Onboarding Reminders',
                type: 'basic',
                icon: 'heroicons_outline:chat-bubble-left-right',
                link: '/onboarding-reminders',
                moduleId: 74,
            },
            {
                id: 'campaign-channels',
                title: 'Campaign Channels',
                type: 'basic',
                icon: 'heroicons_outline:rocket-launch',
                link: '/campaign-channels',
                moduleId: 52,
            },
            {
                id: 'notification-methods',
                title: 'Notification Methods',
                type: 'basic',
                icon: 'heroicons_outline:queue-list',
                link: '/notification-methods',
                moduleId: 66,
            },
            {
                id: 'communication-templates',
                title: 'Templates',
                type: 'collapsable',
                icon: 'heroicons_outline:document-duplicate',
                children: [
                    {
                        id: 'email-templates',
                        title: 'Email Templates',
                        type: 'basic',
                        icon: 'heroicons_outline:envelope',
                        link: '/email-templates',
                        moduleId: 60, // Email Templates
                    },
                    {
                        id: 'whatsapp-templates',
                        title: 'WhatsApp Templates',
                        type: 'basic',
                        icon: 'heroicons_outline:chat-bubble-left-right',
                        link: '/whatsapp-templates',
                        moduleId: 66,
                    },
                    {
                        id: 'sms-templates',
                        title: 'SMS Templates',
                        type: 'basic',
                        icon: 'heroicons_outline:chat-bubble-left',
                        link: '/sms-templates',
                        moduleId: 66,
                    },
                ],
            },
        ],
    },

    {
        id: 'content',
        title: 'Content',
        type: 'collapsable',
        icon: 'heroicons_outline:document-text',
        children: [
            {
                id: 'blogs',
                title: 'Blog Posts',
                type: 'basic',
                icon: 'heroicons_outline:document-text',
                link: '/blogs',
                moduleId: 12, // Blog Posts
            },
            {
                id: 'blogtype',
                title: 'Blog Types',
                type: 'basic',
                icon: 'heroicons_outline:folder',
                link: '/blogtype',
                moduleId: 13, // Blog Types
            },
            {
                id: 'testimonital',
                title: 'Testimonials',
                type: 'basic',
                icon: 'heroicons_outline:star',
                link: '/testimonital',
                moduleId: 17, // Testimonials
            },
            {
                id: 'feedback',
                title: 'Feedback',
                type: 'basic',
                icon: 'heroicons_outline:chat-bubble-left-right',
                link: '/feedback',
                moduleId: 18, // Feedback
            },
        ],
    },

    {
        id: 'users-group',
        title: 'Users & Roles',
        type: 'collapsable',
        icon: 'heroicons_outline:users',
        children: [
            {
                id: 'users',
                title: 'Users',
                type: 'basic',
                icon: 'heroicons_outline:users',
                link: '/users',
                moduleId: 21, // Users
            },
            {
                id: 'user-roles',
                title: 'User Roles',
                type: 'basic',
                icon: 'heroicons_outline:user-circle',
                link: '/user-roles',
                moduleId: 22, // User Roles
            },
        ],
    },

    {
        id: 'report',
        title: 'Reports',
        type: 'collapsable',
        icon: 'heroicons_mini:list-bullet',
        moduleId: 23,// Reports (entire section)
        children: [
            {
                // Reports about sale partners — payouts today, more later.
                // Its own folder rather than a loose entry so the Reports
                // section keeps reading as folders-of-reports.
                id: 'partner-reports',
                title: 'Partner Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                children: [
                    {
                        id: 'partner-payout-report',
                        title: 'Payout Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/partner-payout-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'business-reports',
                title: 'Business Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                children: [
                    {
                        id: 'business-report',
                        title: 'Total Business Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-report',
                        moduleId: 23,
                    },
                    // {
                    //     id: 'business-paidunpaid-report',
                    //     title: 'Business Paid-Unpaid Report',
                    //     type: 'basic',
                    //     icon: 'mat_outline:description', // Heroicons icon for feedback
                    //     link: '/business-paidunpaid-report',
                    // },
                    {
                        id: 'business-growth-report',
                        title: 'Business Growth Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-growth-report',
                        moduleId: 23,
                    },
                    {
                        id: 'business-revenue-report',
                        title: 'Business Revenue Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-revenue-report',
                        moduleId: 23,
                    },
                    {
                        id: 'business-user-engagement-report',
                        title: 'Business User Engagement Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-user-engagement-report',
                        moduleId: 23,
                    },
                    {
                        id: 'business-active-inactive-report',
                        title: 'Business Active Inactive Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-active-inactive-report',
                        moduleId: 23,
                    },
                    {
                        id: 'business-activity-report',
                        title: 'Business Activity Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-activity-report',
                        moduleId: 23,
                    },
                    {
                        id: 'business-registered-report',
                        title: 'Business Registered Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/business-registered-report',
                        moduleId: 23,
                    },
                    // {
                    //     id: 'business-new-vs-existing-report',
                    //     title: 'Business New vs Existing Report',
                    //     type: 'basic',
                    //     icon: 'mat_outline:description', // Heroicons icon for feedback
                    //     link: '/business-new-vs-existing-report',
                    // },
                ],
            },
            {
                id: 'ticket-management-reports',
                title: 'Ticket Management Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                moduleId: 23,
                children: [
                    {
                        id: 'opentickets-report',
                        title: 'Open Tickets Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/opentickets-report',
                        moduleId: 23,
                    },
                    {
                        id: 'ticket-resolution-time-report',
                        title: 'Ticket Resolution Time Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/ticket-resolution-time-report',
                        moduleId: 23,
                    },
                    // {
                    //     id: 'ticket-escalation-report',
                    //     title: 'Ticket Escalation Report',
                    //     type: 'basic',
                    //     icon: 'mat_outline:description', // Heroicons icon for feedback
                    //     link: '/ticket-escalation-report',
                    // },
                    {
                        id: 'ticket-average-response-time-report',
                        title: 'Ticket Average Response Time Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/ticket-average-response-time-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'order-management-reports',
                title: 'Order Management Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                moduleId: 23,
                children: [
                    {
                        id: 'total-orders-report',
                        title: 'Total Orders Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/total-orders-report',
                        moduleId: 23,
                    },
                    {
                        id: 'pending-orders-report',
                        title: 'Pending Orders Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/pending-orders-report',
                        moduleId: 23,
                    },
                    {
                        id: 'order-conversion-rate-report',
                        title: 'Order Conversion Rate Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/order-conversion-rate-report',
                        moduleId: 23,
                    },
                    {
                        id: 'cancelled-failed-order-report',
                        title: 'Cancelled/Failed Order Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/cancelled-failed-order-report',
                        moduleId: 23,
                    },
                    {
                        id: 'sales-report',
                        title: 'Sales Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/sales-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'conversion-reports',
                title: 'Conversion Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                moduleId: 23,
                children: [
                    {
                        id: 'signup-to-sale-funnel-report',
                        title: 'Signup-to-Sale Funnel Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/signup-to-sale-funnel-report',
                        moduleId: 23,
                    },
                    {
                        id: 'trial-conversion-report',
                        title: 'Trial Conversion Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/trial-conversion-report',
                        moduleId: 23,
                    },
                    {
                        id: 'sales-by-trial-report',
                        title: 'Sales by Trial Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/sales-by-trial-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'plan-reports',
                title: 'Plan Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                moduleId: 23,
                children: [
                    {
                        id: 'plan-subscription-report',
                        title: 'Plan Subscription Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/plan-subscription-report',
                        moduleId: 23,
                    },
                    {
                        id: 'plan-renewal-expiration-report',
                        title: 'Plan Renewal Expiration Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/plan-renewal-expiration-report',
                        moduleId: 23,
                    },
                    {
                        id: 'plan-expiring-report',
                        title: 'Plan Expiring Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/plan-expiring-report',
                        moduleId: 23,
                    },
                    {
                        id: 'plan-expiring-summary-by-date-report',
                        title: 'Plan Expiring Summary By Date Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/plan-expiring-summary-by-date-report',
                        moduleId: 23,
                    },
                    {
                        id: 'plan-expired-report',
                        title: 'Plan Expired Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/plan-expired-report',
                        moduleId: 23,
                    },
                    {
                        id: 'plan-expired-summary-by-date-report',
                        title: 'Plan Expired Summary By Date Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/plan-expired-summary-by-date-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'inquiries-reports',
                title: 'Inquiries Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                moduleId: 23,
                children: [
                    {
                        id: 'inquiries-overview-report',
                        title: 'Inquiries Overview Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/inquiries-overview-report',
                        moduleId: 23,
                    },
                    {
                        id: 'inquiry-status-distribution-report',
                        title: 'Inquiry Status Distribution Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/inquiry-status-distribution-report',
                        moduleId: 23,
                    },
                    {
                        id: 'inquiry-conversion-report',
                        title: 'Inquiry Conversion Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/inquiry-conversion-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'feedback-reports',
                title: 'Feedback Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:folder',
                moduleId: 23,
                children: [
                    {
                        id: 'feedback-status-report',
                        title: 'Feedback Status Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/feedback-status-report',
                        moduleId: 23,
                    },
                    {
                        id: 'feedback-sentiment-analysis-report',
                        title: 'Feedback Sentiment Analysis Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/feedback-sentiment-analysis-report',
                        moduleId: 23,
                    },
                    {
                        id: 'feedback-response-time-report',
                        title: 'Feedback Response Time Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/feedback-response-time-report',
                        moduleId: 23,
                    },
                    {
                        id: 'feedback-resolution-report',
                        title: 'Feedback Resolution Report',
                        type: 'basic',
                        icon: 'mat_outline:description', // Heroicons icon for feedback
                        link: '/feedback-resolution-report',
                        moduleId: 23,
                    },
                ],
            },
            {
                id: 'referral-reports',
                title: 'Referral Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:user-group',
                children: [
                    {
                        id: 'total-merchant-referrals-report',
                        title: 'Total Merchant Referrals Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/total-merchant-referrals-report',
                        moduleId: 23,
                    },
                    {
                        id: 'merchant-referral-performance-report',
                        title: 'Merchant Referral Performance Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/merchant-referral-performance-report',
                        moduleId: 23,
                    },
                    {
                        id: 'sales-partner-customer-referrals-report',
                        title: 'Sales Partner Customer Referrals Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/sales-partner-customer-referrals-report',
                        moduleId: 23,
                    },
                    {
                        id: 'sales-partner-performance-commission-report',
                        title: 'Sales Partner Performance & Commission Report',
                        type: 'basic',
                        icon: 'mat_outline:description',
                        link: '/sales-partner-performance-commission-report',
                        moduleId: 23,
                    },
                ],
            },
        ],
    },

    {
        id: 'analytics-logs',
        title: 'Analytics & Logs',
        type: 'collapsable',
        icon: 'heroicons_outline:chart-bar',
        children: [
            {
                id: 'data-usage-analysis',
                title: 'Data Usage Analysis',
                type: 'basic',
                icon: 'analytics',
                link: '/data-usage-analysis',
                moduleId: 2, // Data Usage Analysis
            },
            {
                id: 'activity-logs',
                title: 'Activity Logs',
                type: 'basic',
                icon: 'heroicons_outline:chart-pie',
                link: '/activity-logs',
                moduleId: 1,
            },
            {
                id: 'change-logs',
                title: 'Change Logs',
                type: 'basic',
                icon: 'history',
                link: '/change-logs',
                moduleId: 61,
            },
            {
                id: 'error-logs',
                title: 'Error logs',
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-list',
                link: '/error-logs',
                moduleId: 63,
            },
            {
                // Firebase OTP requests per customer. Rides the Reports grant
                // (23) instead of a new a_modules row, whose id would drift
                // per environment.
                id: 'otp-sends',
                title: 'OTP Sends',
                type: 'basic',
                icon: 'heroicons_outline:device-phone-mobile',
                link: '/otp-sends',
                moduleId: 23,
            },
            {
                id: 'scanned-documents',
                title: 'Scanned Documents',
                type: 'basic',
                icon: 'double_arrow',
                link: '/scanned-documents',
                moduleId: 62,
            },
        ],
    },

    // ---------------------------------------------------------------------
    // System: configuration only. Nothing here is part of a daily workflow,
    // which is why it sits last. Master Data is one level shallower than it
    // used to be (was Admin Settings > Business Settings > Master Data).
    // ---------------------------------------------------------------------
    {
        id: 'system',
        title: 'System',
        type: 'collapsable',
        icon: 'heroicons_outline:cog-8-tooth',
        children: [
            {
                id: 'settings',
                title: 'Settings',
                type: 'basic',
                icon: 'heroicons_outline:cog-8-tooth',
                link: '/settings',
                moduleId: 24, // Settings
            },
            {
                // What Easy Khata pays outside vendors. Same grant as Settings.
                id: 'third-party-services',
                title: 'Third-Party Services',
                type: 'basic',
                icon: 'heroicons_outline:banknotes',
                link: '/third-party-services',
                moduleId: 24, // Settings
            },
            {
                id: 'app-maintenance',
                title: 'App Maintenance',
                type: 'basic',
                icon: 'heroicons_outline:wrench-screwdriver',
                link: '/app-maintenance',
                moduleId: 64,
            },
            {
                id: 'app-configuration',
                title: 'App Configuration',
                type: 'collapsable',
                icon: 'heroicons_outline:adjustments-horizontal',
                // No moduleId - will be visible if any children are visible
                children: [
                    // Website / Business / Business User Settings are the same
                    // screen pointed at three scopes of the settings-flag store
                    // (saasWebsiteSettingAdmin, saasBusinessSettings,
                    // saasBusinessUserSettings), so they belong side by side —
                    // "Website Settings" is not marketing-site content.
                    {
                        id: 'website-setting',
                        title: 'Website Settings',
                        type: 'basic',
                        icon: 'heroicons_outline:globe-alt',
                        link: '/website-setting',
                        moduleId: 25, // Website Settings
                    },
                    {
                        id: 'business-settings',
                        title: 'Business Settings',
                        type: 'basic',
                        icon: 'heroicons_outline:cog-8-tooth',
                        link: '/business-settings',
                        moduleId: 33, // Business Settings
                    },
                    {
                        id: 'business-user-settings',
                        title: 'Business User Settings',
                        type: 'basic',
                        icon: 'heroicons_outline:cog-8-tooth',
                        link: '/business-user-settings',
                        moduleId: 32, // Business User Settings
                    },
                    {
                        id: 'business-roles',
                        title: 'Business Roles',
                        type: 'basic',
                        icon: 'heroicons_outline:user-circle',
                        link: '/business-roles',
                        moduleId: 31, // Business Roles
                    },
                    {
                        id: 'business-category-settings',
                        title: 'Business Category Settings',
                        type: 'basic',
                        icon: 'heroicons_outline:cog-8-tooth',
                        link: '/business-category-settings',
                        moduleId: 46, // Business Categories
                    },
                    {
                        id: 'category-module-map',
                        title: 'Category Module Map',
                        type: 'basic',
                        icon: 'heroicons_outline:squares-2x2',
                        link: '/category-module-map',
                        moduleId: 76, // Category Module Map (p91)
                    },
                    {
                        id: 'invoice-templates',
                        title: 'Invoice Templates',
                        type: 'basic',
                        icon: 'heroicons_outline:document-text',
                        link: '/invoice-templates',
                        moduleId: 30, // Invoice Templates
                    },
                ],
            },
            {
                id: 'access-control',
                title: 'Access Control',
                type: 'collapsable',
                icon: 'heroicons_outline:shield-check',
                children: [
                    {
                        id: 'module-group',
                        title: 'Module Groups',
                        type: 'basic',
                        icon: 'heroicons_outline:building-library',
                        link: '/module-group',
                        moduleId: 34, // Module Groups
                    },
                    {
                        id: 'modules',
                        title: 'Modules',
                        type: 'basic',
                        icon: 'heroicons_outline:folder',
                        link: '/modules',
                        moduleId: 35, // Modules
                    },
                    {
                        id: 'features',
                        title: 'Features',
                        type: 'basic',
                        icon: 'heroicons_outline:building-library',
                        link: '/features',
                        moduleId: 36, // Features
                    },
                    {
                        id: 'feature-category',
                        title: 'Feature Categories',
                        type: 'basic',
                        icon: 'heroicons_outline:folder',
                        link: '/feature-category',
                        moduleId: 37, // Feature Categories
                    },
                    // Dashboard "new module" cards. Rides on the Modules grant
                    // (a_modules 35): a launch is "say something about a module".
                    {
                        id: 'module-launches',
                        title: 'Module Launches',
                        type: 'basic',
                        icon: 'heroicons_outline:megaphone',
                        link: '/module-launches',
                        moduleId: 35, // Modules
                    },
                    // The four above manage what BUSINESS users see in the app
                    // (modulegroups / modules). These two manage what ADMIN
                    // users see in this panel (a_modulegroups / a_modules).
                    {
                        id: 'admin-module-group',
                        title: 'Admin Module Groups',
                        type: 'basic',
                        icon: 'heroicons_outline:rectangle-group',
                        link: '/admin-module-group',
                        moduleId: ADMIN_MODULE_IDS.ADMIN_MODULE_GROUPS,
                    },
                    {
                        id: 'admin-modules',
                        title: 'Admin Modules',
                        type: 'basic',
                        icon: 'heroicons_outline:shield-check',
                        link: '/admin-modules',
                        moduleId: ADMIN_MODULE_IDS.ADMIN_MODULES,
                    },
                ],
            },
            {
                id: 'master-data',
                title: 'Master Data',
                type: 'collapsable',
                icon: 'heroicons_outline:rectangle-stack',
                children: [
                    {
                        id: 'units',
                        title: 'Units',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/units',
                        moduleId: 38,
                    },
                    {
                        id: 'expense-categories',
                        title: 'Expense Categories',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/expense-categories',
                        moduleId: 43,
                    },
                    {
                        id: 'gst-slabs',
                        title: 'GST Slabs',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/gst-slabs',
                        moduleId: 44,
                    },
                    {
                        id: 'hsn-codes',
                        title: 'HSN Codes',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/hsn-codes',
                        moduleId: 45,
                    },
                    {
                        id: 'tax-types',
                        title: 'Tax Types',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/tax-types',
                        moduleId: 53,
                    },
                    {
                        id: 'banks',
                        title: 'Banks',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/banks',
                        moduleId: 56,
                    },
                    {
                        id: 'account-types',
                        title: 'Account Types',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/account-types',
                        moduleId: 60,
                    },
                    {
                        id: 'currencies',
                        title: 'Currencies',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/currencies',
                        moduleId: 51,
                    },
                    {
                        id: 'business-categories',
                        title: 'Business Categories',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/business-categories',
                        moduleId: 46,
                    },
                    {
                        id: 'business-types',
                        title: 'Business Types',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/business-types',
                        moduleId: 47,
                    },
                    {
                        id: 'business-questions',
                        title: 'Business Questions',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/business-questions',
                        moduleId: 48,
                    },
                    {
                        id: 'regions',
                        title: 'Regions',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/regions',
                        moduleId: 49,
                    },
                    {
                        id: 'leave-types',
                        title: 'Leave Types',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/leave-types',
                        moduleId: 50,
                    },
                    {
                        id: 'stock-notes',
                        title: 'Stock Notes',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/stock-notes',
                        moduleId: 59,
                    },
                    {
                        id: 'platforms',
                        title: 'Platforms',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/platforms',
                        moduleId: 54,
                    },
                    {
                        id: 'auth-providers',
                        title: 'Auth providers',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/auth-providers',
                        moduleId: 55,
                    },
                    {
                        id: 'languages',
                        title: 'Languages',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/languages',
                        moduleId: 61,
                    },
                    {
                        id: 'plan-users',
                        title: 'Plan Users',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/plan-users',
                        moduleId: 57,
                    },
                    {
                        id: 'plan-companies',
                        title: 'Plan Companies',
                        type: 'basic',
                        icon: 'double_arrow',
                        link: '/plan-companies',
                        moduleId: 58,
                    },
                ],
            },
        ],
    },
];

export const compactNavigation: ExtendedFuseNavigationItem[] = [
    ...defaultNavigation,
];
export const futuristicNavigation: ExtendedFuseNavigationItem[] = [
    ...defaultNavigation,
];
export const horizontalNavigation: ExtendedFuseNavigationItem[] = [
    ...defaultNavigation,
];
